import * as THREE from "three";

/**
 * Per-mesh material book-keeping:
 *   - the original material (as a clone, never shared)
 *   - the cloned-on-fly working material that we mutate (opacity, emissive)
 *
 * Three.js stores materials by reference; mutating shared materials affects
 * every mesh that uses them. We clone once at registration time so each
 * orderable mesh has its own working copy.
 */
export type MaterialState = {
  base: THREE.MeshStandardMaterial;     // pristine baseline (cloned)
  working: THREE.MeshStandardMaterial;  // the one assigned to the mesh
  baseOpacity: number;
  baseTransparent: boolean;
  baseEmissive: THREE.Color;
  baseEmissiveIntensity: number;
};

const states = new WeakMap<THREE.Mesh, MaterialState>();

export function registerMesh(mesh: THREE.Mesh): MaterialState {
  const existing = states.get(mesh);
  if (existing) return existing;

  const orig = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as
    | THREE.MeshStandardMaterial
    | undefined;

  const base =
    orig && (orig as THREE.Material).isMaterial
      ? (orig.clone() as THREE.MeshStandardMaterial)
      : new THREE.MeshStandardMaterial({ color: 0xcccccc });

  const working = base.clone();
  working.transparent = true; // we tween opacity; keep transparent for crossfade
  working.depthWrite = true;
  mesh.material = working;

  const state: MaterialState = {
    base,
    working,
    baseOpacity: working.opacity,
    baseTransparent: base.transparent,
    baseEmissive: working.emissive.clone(),
    baseEmissiveIntensity: working.emissiveIntensity ?? 1,
  };
  states.set(mesh, state);
  return state;
}

export function getState(mesh: THREE.Mesh): MaterialState | undefined {
  return states.get(mesh);
}
